import type { ActKc2StatusEnum, BimActKc2OtherStatusEnum, WorkOrderStatusEnum } from '@gql/graphql';
import { getUserProfile } from 'app/selectors';

import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import type { IUseAccess } from './types';

const AUTHORITY_MATRIX: Record<string, Record<string, string[]>> = {
    project: {
        read: [
            'OCExpert',
            'OCUser',
            'IBAdministrator',
            'NTCAdministrator',
            'UKSUser',
            'SMRContractor',
            'OKUser',
            'DOAdministrator',
        ],
        create: ['OCUser', 'DOAdministrator'],
        edit: ['OCUser', 'DOAdministrator'],
        save: ['OCUser', 'DOAdministrator'],
        delete: ['OCUser', 'DOAdministrator'],
    },
    contract: {
        read: [
            'OCExpert',
            'OCUser',
            'IBAdministrator',
            'NTCAdministrator',
            'UKSUser',
            'SMRContractor',
            'OKUser',
            'DOAdministrator',
        ],
        create: ['OCUser', 'DOAdministrator'],
        edit: ['OCUser', 'DOAdministrator'],
        exportExcel: ['OCExpert', 'OCUser', 'UKSUser', 'SMRContractor', 'OKUser', 'DOAdministrator'],
        delete: ['OCUser', 'DOAdministrator'],
    },
    contractBim: {
        read: [
            'OCExpert',
            'OCUser',
            'IBAdministrator',
            'NTCAdministrator',
            'UKSUser',
            'SMRContractor',
            'OKUser',
            'DOAdministrator',
        ],
        create: ['OCUser', 'DOAdministrator'],
        edit: ['OCUser', 'DOAdministrator'],
        delete: ['OCUser', 'DOAdministrator'],
        createContractStructure: ['OCUser', 'UKSUser', 'DOAdministrator'],
    },
    priceList: {
        read: [
            'OCExpert',
            'OCUser',
            'IBAdministrator',
            'NTCAdministrator',
            'UKSUser',
            'SMRContractor',
            'OKUser',
            'DOAdministrator',
        ],
        create: ['OCUser', 'DOAdministrator'],
        edit: ['OCUser', 'DOAdministrator'],
        save: ['OCUser', 'DOAdministrator'],
        delete: ['OCUser', 'DOAdministrator'],
    },
    order: {
        read: [
            'OCExpert',
            'OCUser',
            'IBAdministrator',
            'NTCAdministrator',
            'UKSUser',
            'SMRContractor',
            'OKUser',
            'DOAdministrator',
        ],
        create: ['OCUser', 'DOAdministrator', 'SMRContractor'],
        edit: ['OCUser', 'DOAdministrator', 'SMRContractor'],
        editObjectCode: ['OCUser', 'DOAdministrator'],
        editCompletedStatus: ['OCUser', 'DOAdministrator'],
        exportExcel: ['OCUser', 'SMRContractor', 'DOAdministrator'],
        delete: ['OCUser', 'DOAdministrator'],
        rollbackWorkOrderChange: ['OCUser', 'DOAdministrator'],
    },
    orderBim: {
        read: [
            'OCExpert',
            'OCUser',
            'IBAdministrator',
            'NTCAdministrator',
            'UKSUser',
            'SMRContractor',
            'OKUser',
            'DOAdministrator',
        ],
        create: ['OCUser', 'UKSUser', 'SMRContractor', 'DOAdministrator'],
        edit: ['OCUser', 'UKSUser', 'OKUser', 'SMRContractor', 'DOAdministrator'],
        editOtherDistribution_ON_FORMATION: ['OCUser', 'UKSUser', 'OKUser', 'SMRContractor', 'DOAdministrator'],
        editOtherDistribution_IN_PROGRESS: ['OCExpert'],
        editStructure: ['OCUser', 'OKUser', 'DOAdministrator'],
        editStatus: ['OCUser', 'UKSUser', 'SMRContractor', 'DOAdministrator'],
        addRecords: ['OCUser', 'UKSUser', 'OKUser', 'SMRContractor', 'DOAdministrator'],
        createManualOther: ['OCUser', 'DOAdministrator'],
        exportExcel: ['OCExpert', 'OCUser', 'SMRContractor', 'DOAdministrator'],
        save: ['OCUser', 'DOAdministrator'],
        delete: ['OCUser', 'DOAdministrator'],
    },
    catalogs: {
        read: [
            'OCExpert',
            'OCUser',
            'IBAdministrator',
            'NTCAdministrator',
            'UKSUser',
            'SMRContractor',
            'OKUser',
            'DOAdministrator',
        ],
        create: ['NTCAdministrator', 'DOAdministrator', 'OCExpert'],
        createLimited: ['NTCAdministrator', 'OCExpert'],
        editLimited: ['NTCAdministrator', 'OCExpert'],
        deleteLimited: ['NTCAdministrator', 'OCExpert'],
        edit: ['NTCAdministrator', 'DOAdministrator', 'OCExpert', 'OCUser'],
        delete: ['NTCAdministrator', 'DOAdministrator', 'OCExpert'],
    },
    additionalAgreement: {
        read: [
            'OCExpert',
            'OCUser',
            'IBAdministrator',
            'NTCAdministrator',
            'UKSUser',
            'SMRContractor',
            'OKUser',
            'DOAdministrator',
        ],
        create: ['OCUser', 'DOAdministrator'],
        edit: ['OCUser', 'DOAdministrator'],
        editStatus: ['OCUser', 'DOAdministrator'],
        uploadToExcel: ['OCUser', 'DOAdministrator', 'OCExpert', 'SMRContractor'],
        delete: ['OCUser', 'DOAdministrator'],
    },
    statement: {
        read: [
            'OCExpert',
            'OCUser',
            'IBAdministrator',
            'NTCAdministrator',
            'UKSUser',
            'SMRContractor',
            'OKUser',
            'DOAdministrator',
        ],
        create: ['IBAdministrator', 'NTCAdministrator', 'DOAdministrator'],
        edit: ['IBAdministrator', 'NTCAdministrator', 'DOAdministrator'],
        save: ['IBAdministrator', 'NTCAdministrator', 'DOAdministrator'],
        close: ['IBAdministrator', 'NTCAdministrator', 'DOAdministrator'],
        delete: ['IBAdministrator', 'NTCAdministrator', 'DOAdministrator'],
    },
    aktKC2: {
        read: [
            'OCExpert',
            'OCUser',
            'IBAdministrator',
            'NTCAdministrator',
            'UKSUser',
            'SMRContractor',
            'OKUser',
            'DOAdministrator',
        ],
        create: ['OCUser', 'SMRContractor', 'DOAdministrator'],
        edit: ['OCUser', 'SMRContractor', 'DOAdministrator'],
        editCompletedStatus: ['OCUser', 'DOAdministrator'],
        unload: ['OCExpert', 'OCUser', 'UKSUser', 'OKUser', 'SMRContractor', 'DOAdministrator'],
        delete: ['OCUser', 'UKSUser', 'SMRContractor', 'DOAdministrator'],
        uploadActTemplate: ['DOAdministrator', 'OCUser', 'UKSUser', 'SMRContractor'],
        downloadActTemplate: ['DOAdministrator', 'OCUser', 'UKSUser', 'SMRContractor'],
    },
    aktKC2BimOther: {
        read: [
            'OCExpert',
            'OCUser',
            'IBAdministrator',
            'NTCAdministrator',
            'UKSUser',
            'SMRContractor',
            'OKUser',
            'DOAdministrator',
        ],
        create: ['DOAdministrator', 'OCUser', 'SMRContractor'],
        edit_IN_PROGRESS: ['DOAdministrator', 'OCUser', 'SMRContractor'],
        edit_ON_CHECK: ['DOAdministrator', 'OCUser'],
        edit_ACCEPTED: ['DOAdministrator', 'OCUser'],
        editStatus: ['DOAdministrator', 'OCUser', 'SMRContractor'],
        delete: ['DOAdministrator', 'OCUser', 'SMRContractor'],
    },
    aktKC2Bim: {
        read: [
            'OCExpert',
            'OCUser',
            'IBAdministrator',
            'NTCAdministrator',
            'UKSUser',
            'SMRContractor',
            'OKUser',
            'DOAdministrator',
        ],
        create: ['DOAdministrator', 'OCUser'],
        delete: ['DOAdministrator', 'OCUser'],
    },
    workOrderChangeUer: {
        read: [
            'OCExpert',
            'OCUser',
            'IBAdministrator',
            'NTCAdministrator',
            'UKSUser',
            'SMRContractor',
            'OKUser',
            'DOAdministrator',
        ],
        create: ['DOAdministrator', 'OCUser', 'SMRContractor'],
        createClosing: ['DOAdministrator', 'OCUser'],
        edit: ['DOAdministrator', 'OCUser', 'SMRContractor'],
        editStructure: ['DOAdministrator', 'OCUser', 'UKSUser'],
        editObjectCode: ['OCUser', 'DOAdministrator'],
        editCompletedStatus: ['DOAdministrator', 'OCUser'],
        addingEntries: ['DOAdministrator', 'OCUser', 'SMRContractor'],
        manualCreation: ['DOAdministrator', 'OCUser', 'UKSUser', 'OKUser'],
        delete: ['DOAdministrator', 'OCUser'],
        massAddCoef: ['DOAdministrator', 'OCUser'],
    },
    aktKC3: {
        read: [
            'NTCAdministrator',
            'IBAdministrator',
            'DOAdministrator',
            'OCExpert',
            'OCUser',
            'UKSUser',
            'OKUser',
            'SMRContractor',
        ],
        create: ['DOAdministrator', 'OCUser', 'SMRContractor'],
        edit: ['DOAdministrator', 'OCUser', 'SMRContractor'],
        exportExcel: ['DOAdministrator', 'OCExpert', 'OCUser', 'SMRContractor'],
    },
    catalogTypesOfWork: {
        read: [
            'NTCAdministrator',
            'IBAdministrator',
            'DOAdministrator',
            'OCExpert',
            'OCUser',
            'UKSUser',
            'OKUser',
            'SMRContractor',
        ],
        create: ['NTCAdministrator', 'DOAdministrator', 'OCUser'],
        edit: ['NTCAdministrator', 'DOAdministrator', 'OCUser'],
        delete: ['NTCAdministrator', 'DOAdministrator', 'OCUser'],
    },
    catalogPrice: {
        read: [
            'NTCAdministrator',
            'IBAdministrator',
            'DOAdministrator',
            'OCExpert',
            'OCUser',
            'UKSUser',
            'OKUser',
            // https://conf.ibs.ru/pages/viewpage.action?pageId=373129158#id-Отображениесборникарасценок-Таблица1.Ролеваямодель
            // 'SMRContractor',
        ],
        create: ['NTCAdministrator'],
        edit: ['NTCAdministrator'],
        delete: ['NTCAdministrator'],
        export: ['NTCAdministrator', 'DOAdministrator', 'OCUser', 'OCExpert'],
    },
    catalogRatios: {
        read: ['NTCAdministrator', 'IBAdministrator', 'OCExpert', 'OCUser', 'DOAdministrator', 'UKSUser', 'OKUser'],
        create: ['NTCAdministrator'],
        edit: ['NTCAdministrator'],
        delete: ['NTCAdministrator'],
    },
    catalogOtherCostTemplates: {
        read: ['NTCAdministrator', 'IBAdministrator', 'OCExpert', 'OCUser', 'DOAdministrator', 'UKSUser', 'OKUser'],
        create: ['NTCAdministrator'],
        edit: ['NTCAdministrator'],
        delete: ['NTCAdministrator'],
    },
    calculation: {
        read: ['OCExpert', 'OCUser', 'IBAdministrator', 'NTCAdministrator', 'UKSUser', 'OKUser', 'DOAdministrator'],
        create: ['OCUser', 'DOAdministrator'],
        edit: ['OCUser', 'DOAdministrator'],
        save: ['OCUser', 'DOAdministrator'],
    },
    calculationPrices: {
        read: [
            'NTCAdministrator',
            'IBAdministrator',
            'DOAdministrator',
            'OCExpert',
            'OCUser',
            'UKSUser',
            'OKUser',
            'SMRContractor',
        ],
        copyCollectionUER: ['DOAdministrator', 'OCUser'],
        addCollectionUERItemsFromCatalog: ['DOAdministrator', 'OCUser'],
        removeCollectionUERPrice: ['DOAdministrator', 'OCUser'],
        removeAllCollectionUER: ['DOAdministrator', 'OCUser'],
        uploadCollectionUERXml: ['DOAdministrator', 'OCUser'],
        export: ['DOAdministrator', 'OCExpert', 'OCUser'],
        importWorkTypes: ['NTCAdministrator', 'OCUser', 'DOAdministrator'],
    },
    calculationWorkVolume: {
        read: ['NTCAdministrator', 'IBAdministrator', 'OCExpert', 'OCUser', 'DOAdministrator', 'UKSUser', 'OKUser'],
        create: ['OCUser', 'DOAdministrator', 'UKSUser'],
        edit: ['OCUser', 'DOAdministrator', 'UKSUser'],
        addOrEditMtrs: ['OCUser', 'DOAdministrator', 'UKSUser', 'OKUser'],
        delete: ['DOAdministrator', 'OCUser', 'UKSUser'],
        deleteMtrs: ['DOAdministrator', 'OCUser', 'UKSUser', 'OKUser'],
        export: ['DOAdministrator', 'OCExpert', 'OCUser', 'UKSUser'],
    },
    calculationOthers: {
        read: ['NTCAdministrator', 'IBAdministrator', 'OCExpert', 'OCUser', 'DOAdministrator', 'UKSUser', 'OKUser'],
        create: ['OCUser', 'DOAdministrator'],
        edit: ['OCUser', 'DOAdministrator'],
        delete: ['OCUser', 'DOAdministrator'],
        uploadTemplates: ['OCUser', 'DOAdministrator'],
    },
    calculationRatios: {
        read: ['NTCAdministrator', 'IBAdministrator', 'OCExpert', 'OCUser', 'DOAdministrator', 'UKSUser', 'OKUser'],
        create: ['OCUser', 'DOAdministrator'],
        edit: ['OCUser', 'DOAdministrator'],
        delete: ['OCUser', 'DOAdministrator'],
    },
    calculationMtrs: {
        read: ['NTCAdministrator', 'IBAdministrator', 'OCExpert', 'OCUser', 'DOAdministrator', 'UKSUser', 'OKUser'],
        create: ['OCUser', 'DOAdministrator', 'OKUser'],
        edit: ['OCUser', 'DOAdministrator', 'OKUser'],
        delete: ['OCUser', 'DOAdministrator', 'OKUser'],
    },
    calculationStructure: {
        read: ['NTCAdministrator', 'IBAdministrator', 'OCExpert', 'OCUser', 'DOAdministrator', 'UKSUser', 'OKUser'],
        createOrEdit: ['OCUser', 'DOAdministrator', 'UKSUser'],
    },
    calculationLimited: {
        read: ['OCExpert', 'OCUser', 'IBAdministrator', 'NTCAdministrator', 'UKSUser', 'OKUser', 'DOAdministrator'],
        create: ['OCUser', 'DOAdministrator'],
        edit: ['OCUser', 'DOAdministrator'],
        save: ['OCUser', 'DOAdministrator'],
        delete: ['OCUser', 'DOAdministrator'],
    },
    calculationPreview: {
        read: ['OCExpert', 'OCUser', 'IBAdministrator', 'NTCAdministrator', 'UKSUser', 'OKUser', 'DOAdministrator'],
        create: ['OCUser', 'DOAdministrator'],
        edit: ['OCUser', 'DOAdministrator'],
        export: ['OCUser', 'DOAdministrator', 'OCExpert'],
    },
    bimCalculationStructure: {
        read: ['OCExpert', 'OCUser', 'IBAdministrator', 'NTCAdministrator', 'UKSUser', 'OKUser', 'DOAdministrator'],
        create: ['OCUser', 'DOAdministrator'],
        edit: ['OCUser', 'DOAdministrator'],
        delete: ['OCUser', 'DOAdministrator'],
    },
};

const checkValues = (key: string, rules: Record<string, string[]>, roles: string[]) => {
    if (!rules?.[key]?.length || !roles?.length) {
        return false;
    }

    for (let i = 0; i < rules[key].length; i++) {
        if (roles.some((role) => role === rules[key][i])) {
            return true;
        }
    }

    return false;
};

const ruleChecker = (
    section: string,
    roles: string[],
    entityStatus?: {
        statusKC2?: ActKc2StatusEnum | BimActKc2OtherStatusEnum;
        workOrderStatus?: WorkOrderStatusEnum;
    },
) => ({
    canRead: checkValues('read', AUTHORITY_MATRIX[section], roles),
    canCreate: checkValues('create', AUTHORITY_MATRIX[section], roles),
    canCreateClosing: checkValues('createClosing', AUTHORITY_MATRIX[section], roles),
    canEdit: checkValues('edit', AUTHORITY_MATRIX[section], roles),
    canEditOtherDistribution: checkValues(
        `editOtherDistribution_${entityStatus?.workOrderStatus}`,
        AUTHORITY_MATRIX[section],
        roles,
    ),
    canEditStructure: checkValues('editStructure', AUTHORITY_MATRIX[section], roles),
    canEditObjectCode: checkValues('editObjectCode', AUTHORITY_MATRIX[section], roles),
    canEditStatus: checkValues('editStatus', AUTHORITY_MATRIX[section], roles),
    canEditCompletedStatus: checkValues('editCompletedStatus', AUTHORITY_MATRIX[section], roles),
    canManualCreation: checkValues('manualCreation', AUTHORITY_MATRIX[section], roles),
    canEditActKC2: checkValues(
        `edit${entityStatus?.statusKC2 ? `_${entityStatus?.statusKC2}` : ''}`,
        AUTHORITY_MATRIX[section],
        roles,
    ),
    canAddRecords: checkValues('addRecords', AUTHORITY_MATRIX[section], roles),
    canCreateManualOther: checkValues('createManualOther', AUTHORITY_MATRIX[section], roles),
    canExportExcel: checkValues('exportExcel', AUTHORITY_MATRIX[section], roles),
    canSave: checkValues('save', AUTHORITY_MATRIX[section], roles),
    canDelete: checkValues('delete', AUTHORITY_MATRIX[section], roles),
    canClose: checkValues('close', AUTHORITY_MATRIX[section], roles),
    canUnload: checkValues('unload', AUTHORITY_MATRIX[section], roles),
    canUploadActTemplate: checkValues('uploadActTemplate', AUTHORITY_MATRIX[section], roles),
    canDownloadActTemplate: checkValues('downloadActTemplate', AUTHORITY_MATRIX[section], roles),
    canCreateContractStructure: checkValues('createContractStructure', AUTHORITY_MATRIX[section], roles),
    canUploadToExcel: checkValues('uploadToExcel', AUTHORITY_MATRIX[section], roles),
    canAddingEntries: checkValues('addingEntries', AUTHORITY_MATRIX[section], roles),
    canAddMassCoef: checkValues('massAddCoef', AUTHORITY_MATRIX[section], roles),
    canRollbackWorkOrderChange: checkValues('rollbackWorkOrderChange', AUTHORITY_MATRIX[section], roles),
    canCalculationCopyCollectionUER: checkValues('copyCollectionUER', AUTHORITY_MATRIX[section], roles),
    canCalculationRemoveAllCollectionUER: checkValues('removeAllCollectionUER', AUTHORITY_MATRIX[section], roles),
    canCalculationRemoveCollectionUERPrice: checkValues('removeCollectionUERPrice', AUTHORITY_MATRIX[section], roles),
    canCalculationUploadCollectionUERXml: checkValues('uploadCollectionUERXml', AUTHORITY_MATRIX[section], roles),
    canCalculationAddCollectionUERItemsFromCatalog: checkValues(
        'addCollectionUERItemsFromCatalog',
        AUTHORITY_MATRIX[section],
        roles,
    ),
    canCalculationOthersUploadTemplates: checkValues('uploadTemplates', AUTHORITY_MATRIX[section], roles),
    canCreateLimited: checkValues('createLimited', AUTHORITY_MATRIX[section], roles),
    canEditLimited: checkValues('editLimited', AUTHORITY_MATRIX[section], roles),
    canDeleteLimited: checkValues('deleteLimited', AUTHORITY_MATRIX[section], roles),
    canCalculationPreviewRead: checkValues('read', AUTHORITY_MATRIX[section], roles),
    canCalculationPreviewCreate: checkValues('create', AUTHORITY_MATRIX[section], roles),
    canCalculationPreviewEdit: checkValues('edit', AUTHORITY_MATRIX[section], roles),
    canExport: checkValues('export', AUTHORITY_MATRIX[section], roles),
    canAddOrEditMtrs: checkValues('addOrEditMtrs', AUTHORITY_MATRIX[section], roles),
    canDeleteMtrs: checkValues('deleteMtrs', AUTHORITY_MATRIX[section], roles),
    canCreateOrEdit: checkValues('createOrEdit', AUTHORITY_MATRIX[section], roles),
    canImportWorkTypes: checkValues('importWorkTypes', AUTHORITY_MATRIX[section], roles),
});

/**
 * Администратор НТЦ - NTCAdministrator *
 * Администратор ИБ - IBAdministrator *
 * Администратор ДО - DOAdministrator *
 * Эксперт ОЦ - OCExpert *
 * ОЦ - OCUser *
 * УКС - UKSUser *
 * ОК - OKUser *
 * Подрядчик СМР - SMRContractor *
 @returns {Object} Объект с правами для текущего пользователя
 */
export const useAccess = ({
    statusKC2,
    workOrderStatus,
}: {
    statusKC2?: ActKc2StatusEnum | BimActKc2OtherStatusEnum;
    workOrderStatus?: WorkOrderStatusEnum;
} = {}): IUseAccess => {
    const profileInfo = useSelector(getUserProfile) as { roles: string[] };
    const roles = profileInfo?.roles;

    const projectRules = useMemo<IUseAccess['project']>(() => ruleChecker('project', roles), [roles]);
    const contractRules = useMemo<IUseAccess['contract']>(() => ruleChecker('contract', roles), [roles]);
    const contractBimRules = useMemo<IUseAccess['contractBim']>(() => ruleChecker('contractBim', roles), [roles]);
    const priceListRules = useMemo<IUseAccess['priceList']>(() => ruleChecker('priceList', roles), [roles]);
    const orderUerRules = useMemo<IUseAccess['order']>(() => ruleChecker('order', roles), [roles]);
    const orderBimRules = useMemo<IUseAccess['orderBim']>(
        () => ruleChecker('orderBim', roles, { workOrderStatus }),
        [roles, workOrderStatus],
    );
    const catalogsRules = useMemo<IUseAccess['catalogs']>(() => ruleChecker('catalogs', roles), [roles]);
    const additionalAgreementRules = useMemo<IUseAccess['additionalAgreement']>(
        () => ruleChecker('additionalAgreement', roles),
        [roles],
    );
    const workOrderChangeUerRules = useMemo<IUseAccess['workOrderChangeUer']>(
        () => ruleChecker('workOrderChangeUer', roles),
        [roles],
    );
    const catalogTypesOfWorkRules = useMemo<IUseAccess['catalogTypesOfWork']>(
        () => ruleChecker('catalogTypesOfWork', roles),
        [roles],
    );
    const catalogPriceRules = useMemo<IUseAccess['catalogPrice']>(() => ruleChecker('catalogPrice', roles), [roles]);
    const catalogRatiosRules = useMemo<IUseAccess['catalogRatios']>(() => ruleChecker('catalogRatios', roles), [roles]);
    const catalogOtherCostTemplatesRules = useMemo<IUseAccess['catalogOtherCostTemplates']>(
        () => ruleChecker('catalogOtherCostTemplates', roles),
        [roles],
    );
    const aktKC3Rules = useMemo<IUseAccess['aktKC3']>(() => ruleChecker('aktKC3', roles), [roles]);
    const statementRules = useMemo<IUseAccess['statement']>(() => ruleChecker('statement', roles), [roles]);
    const aktKC2Rules = useMemo<IUseAccess['aktKC2']>(() => ruleChecker('aktKC2', roles), [roles]);
    const aktKC2BimOtherRules = useMemo<IUseAccess['aktKC2BimOther']>(
        () => ruleChecker('aktKC2BimOther', roles, { statusKC2 }),
        [roles, statusKC2],
    );
    const aktKC2BimRules = useMemo<IUseAccess['aktKC2Bim']>(() => ruleChecker('aktKC2Bim', roles), [roles]);
    const calculationRules = useMemo<IUseAccess['calculation']>(() => ruleChecker('calculation', roles), [roles]);
    const calculationPricesRules = useMemo<IUseAccess['calculationPrices']>(
        () => ruleChecker('calculationPrices', roles),
        [roles],
    );
    const calculationWorkVolumeRules = useMemo<IUseAccess['calculationWorkVolume']>(
        () => ruleChecker('calculationWorkVolume', roles),
        [roles],
    );
    const calculationOthersRules = useMemo<IUseAccess['calculationOthers']>(
        () => ruleChecker('calculationOthers', roles),
        [roles],
    );
    const calculationRatiosRules = useMemo<IUseAccess['calculationRatios']>(
        () => ruleChecker('calculationRatios', roles),
        [roles],
    );
    const calculationMtrsRules = useMemo<IUseAccess['calculationMtrs']>(
        () => ruleChecker('calculationMtrs', roles),
        [roles],
    );
    const calculationLimitedRules = useMemo<IUseAccess['calculationLimited']>(
        () => ruleChecker('calculationLimited', roles),
        [roles],
    );
    const calculationPreviewRules = useMemo<IUseAccess['calculationPreview']>(
        () => ruleChecker('calculationPreview', roles),
        [roles],
    );
    const calculationStructureRules = useMemo<IUseAccess['calculationStructure']>(
        () => ruleChecker('calculationStructure', roles),
        [roles],
    );
    const bimCalculationStructureRules = useMemo<IUseAccess['bimCalculationStructure']>(
        () => ruleChecker('bimCalculationStructure', roles),
        [roles],
    );

    return {
        project: projectRules,
        contract: contractRules,
        contractBim: contractBimRules,
        priceList: priceListRules,
        order: orderUerRules,
        orderBim: orderBimRules,
        statement: statementRules,
        aktKC2: aktKC2Rules,
        catalogs: catalogsRules,
        additionalAgreement: additionalAgreementRules,
        aktKC2BimOther: aktKC2BimOtherRules,
        aktKC2Bim: aktKC2BimRules,
        workOrderChangeUer: workOrderChangeUerRules,
        aktKC3: aktKC3Rules,
        catalogTypesOfWork: catalogTypesOfWorkRules,
        catalogPrice: catalogPriceRules,
        catalogRatios: catalogRatiosRules,
        catalogOtherCostTemplates: catalogOtherCostTemplatesRules,
        calculation: calculationRules,
        calculationPrices: calculationPricesRules,
        calculationWorkVolume: calculationWorkVolumeRules,
        calculationOthers: calculationOthersRules,
        calculationRatios: calculationRatiosRules,
        calculationMtrs: calculationMtrsRules,
        calculationLimited: calculationLimitedRules,
        calculationPreview: calculationPreviewRules,
        calculationStructure: calculationStructureRules,
        bimCalculationStructure: bimCalculationStructureRules,
    };
};
